import { ILCHttpResponse } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

interface IUser {
  identity: string
  firstName: string
  lastName: string
}
export interface IResultUploadInfo {
  field: string
  value: string
  errors: string[]
}
export interface IResultValidateCSV {
  success: boolean
  existsInDb?: boolean
  uploadInfo: IResultUploadInfo[]
  uploadWarnings?: IResultUploadInfo[]
  user: IUser
}
export interface IValidateCSVResponse {
  statusCode: number
  timestamp: string
  data: {
    validRows: number
    invalidRows: number
    errors: string[]
  }
}

export interface IUploadInfo {
  field: string
  value: string
  errors: string[]
}

export interface IValidationError {
  success: boolean
  uploadInfo: IUploadInfo[]
  user: IUser
}

interface IValidationSuccess {
  total: number
  identities: string
}

interface IValidationErrorSection {
  total: number
  identities: string
  info: IValidationError[]
}

interface IInsertToDBResult {
  inserted: IUser[]
  updated: IUser[]
  errors: any[]
}

export interface IUploadResponse {
  statusCode: number
  timestamp: string
}

export const userActionsApi = createApi({
  reducerPath: 'userActionsApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/user`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  endpoints: (builder) => ({
    downloadSampleCSV: builder.query<Blob, string | undefined>({
      query: (locale) => ({
        url: locale ? `/multiple/download/excel-template?locale=${encodeURIComponent(locale)}` : '/multiple/download/excel-template',
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    validateCSV: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/multiple/upload/validate',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (rawResult: ILCHttpResponse<any>) => {
        return rawResult.data as IValidateCSVResponse
      },
    }),
    uploadEmployees: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/multiple/upload/employees',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (rawResult: ILCHttpResponse<any>) => {
        return rawResult.data as IUploadResponse
      },
    }),
  }),
})

export const { useLazyDownloadSampleCSVQuery, useValidateCSVMutation, useUploadEmployeesMutation } = userActionsApi
